import { HttpStatusCode } from "axios";
import { startTransition, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Subscription } from "rxjs";

import {
  ApiException,
  castErrorResult,
  createCatchErrorResult,
  createProducePollingStream,
  createSuccessResultVoid,
  errors,
  gunzipBase64ToUtf8,
  INDEX_NOT_FOUND,
  IProduceAbortPayload,
  IProduceCheckStatusPayload,
  IProduceFetchLogsPayload,
  IProductionPollGroupState,
  IProductionPollState,
  isAbortError,
  isProducePollingGroupNotRunning,
  isProducePollingGroupRunning,
  isProductionPollStateTerminal,
  isTranslatableItem,
  Log,
  PRODUCE_POLLING_INTERVAL_MS,
  PRODUCE_POLLING_MAX_CONSECUTIVE_ERRORS,
  ProduceJoinApi,
  producePollingRunningCountAtom,
  productionLogStateAtom,
  productionPollGroupStateAtom,
  productionPollIdSelector,
  productionPollStateSelector,
  ProductionStatusEnum,
  productionUiIdSelector,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidApiResponse,
  translateInfoItem,
  warnAndToastException,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useProducePollingCtxProvider = () => {
  const { t } = useTranslation();
  const subscriptionProducePollingRef = useRef<Subscription | null>(null);

  const checkStatusAbortControllerRef = useRef<AbortController | null>(null);
  const abortToastShownRef = useRef(false);
  const consecutivePollingErrorsRef = useRef(0);

  const productionPollId = useRecoilValue(productionPollIdSelector);
  const productionUiId = useRecoilValue(productionUiIdSelector);
  const productionPollState = useRecoilValue(productionPollStateSelector);
  const setLogData = useSetRecoilState(productionLogStateAtom);
  const setPollingCount = useSetRecoilState(producePollingRunningCountAtom);
  const [productionGroupState, setProductionGroupState] = useRecoilState(
    productionPollGroupStateAtom,
  );
  const productionGroupStateRef = useRef(productionGroupState);

  productionGroupStateRef.current = productionGroupState;

  const getNextProductionPollId = useEvent(
    (checkedState: IProductionPollState): number | undefined => {
      const productionIds =
        (productionGroupStateRef.current ?? productionGroupState)
          ?.productionIds || [];
      const productionId = checkedState.productionId;
      const productionIdIndex = productionIds.indexOf(productionId);

      if (productionIdIndex === INDEX_NOT_FOUND) {
        Log.warn(
          "Production id not found in group, skipping find next",
          productionId,
          productionIds,
        );
        return undefined;
      }

      const isTerminal = isProductionPollStateTerminal(checkedState);

      if (!isTerminal) {
        Log.debug(
          "Production not terminal, skipping find next",
          productionId,
          productionIds,
        );
        return undefined;
      }

      const nextProductionIdIndex = productionIdIndex + 1;

      if (nextProductionIdIndex >= productionIds.length) {
        Log.debug(
          "Production id is last in group, skipping find next",
          productionId,
          productionIds,
        );
        return undefined;
      }

      const nextProductionId = productionIds[nextProductionIdIndex];

      Log.debug(`Found next production id in group: ${nextProductionId}`, {
        productionId,
        productionIds,
        nextProductionId,
        nextProductionIdIndex,
      });

      return nextProductionId;
    },
  );

  const updateProductionGroup = useEvent(
    (
      checkedState: IProductionPollState,
      polledProductionId: number,
    ): IProductionPollGroupState | undefined => {
      const currentGroup =
        productionGroupStateRef.current ?? productionGroupState;

      if (!currentGroup) {
        Log.warn(
          "No production group state available, skipping update",
          checkedState,
        );
        return undefined;
      }

      const updatedStates = currentGroup.states.map(state => {
        if (state.productionId === checkedState.productionId) {
          return {
            ...checkedState,
            logsCount: checkedState.logsCount ?? state.logsCount ?? 0,
          };
        }
        return state;
      });

      const nextProductionGroupState: IProductionPollGroupState = {
        ...currentGroup,
        states: updatedStates,
      };

      const nextProductionPollId = getNextProductionPollId(checkedState);

      if (nextProductionPollId) {
        nextProductionGroupState.productionPollId = nextProductionPollId;

        if (currentGroup.productionUiId === polledProductionId) {
          nextProductionGroupState.productionUiId = nextProductionPollId;
        }
      }

      productionGroupStateRef.current = nextProductionGroupState;
      setProductionGroupState(nextProductionGroupState);

      return nextProductionGroupState;
    },
  );

  const resolvePollStateForProductionId = useEvent(
    (targetProductionId: number): IProductionPollState | undefined => {
      const group = productionGroupStateRef.current ?? productionGroupState;
      const fromGroup = group?.states.find(
        state => state.productionId === targetProductionId,
      );

      if (fromGroup) {
        return fromGroup;
      }

      if (targetProductionId === productionPollId) {
        return productionPollState;
      }

      return undefined;
    },
  );

  const apiAbortProduceIfRunning = useEvent(
    async (overrideProductionId?: number): Promise<SimpleResult<void>> => {
      const targetProductionId = overrideProductionId ?? productionPollId;

      if (!targetProductionId) {
        Log.debug("No active production to abort");
        return createSuccessResultVoid();
      }

      const stateForCancel =
        resolvePollStateForProductionId(targetProductionId);

      if (stateForCancel && isProductionPollStateTerminal(stateForCancel)) {
        Log.debug("Production already terminal, skipping cancel api", {
          targetProductionId,
          status: stateForCancel.status,
        });
        return createSuccessResultVoid();
      }

      const payload: IProduceAbortPayload = {
        productionId: targetProductionId,
      };

      Log.debug(
        "Produce abort api payload",
        payload,
        "state",
        stateForCancel ?? productionPollState,
      );

      try {
        const apiResult = await ProduceJoinApi.produceCancel(payload);

        if (!apiResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!apiResult.success) {
          return castErrorResult(apiResult);
        }

        if (apiResult.success && !apiResult.data) {
          return simpleResultInvalidApiResponse(t);
        }

        await apiCheckProduceState(targetProductionId);

        const stateAfterCancel =
          resolvePollStateForProductionId(targetProductionId);

        if (
          stateAfterCancel &&
          !isProductionPollStateTerminal(stateAfterCancel)
        ) {
          await apiCheckProduceState(targetProductionId);
        }

        return createSuccessResultVoid();
      } catch (error) {
        return createCatchErrorResult(error, t);
      }
    },
  );

  const stopAndCleanupProducePolling = useEvent(
    async ({
      updatedState,
    }: {
      updatedState?: IProductionPollState;
    } = {}): Promise<SimpleResult<void>> => {
      try {
        const stateComputed = updatedState ?? productionPollState;
        Log.debug(
          "Stopping produce polling, state ",
          productionPollState,
          "override",
          updatedState,
        );

        if (stateComputed && !isProductionPollStateTerminal(stateComputed)) {
          Log.debug(
            "Previous produce not finished, aborting it-",
            productionPollState,
          );
          const abortProduceResult = await apiAbortProduceIfRunning(
            stateComputed.productionId,
          );

          if (!abortProduceResult.success) {
            warnAndToastSimpleResult(abortProduceResult, t);
            return abortProduceResult;
          }
        }

        return createSuccessResultVoid();
      } catch (error) {
        return createCatchErrorResult(error, t);
      } finally {
        consecutivePollingErrorsRef.current = 0;

        if (!subscriptionProducePollingRef.current?.closed) {
          Log.debug(`Stop produce polling`);
          subscriptionProducePollingRef.current?.unsubscribe();
        }

        subscriptionProducePollingRef.current = null;
        checkStatusAbortControllerRef.current?.abort();
        checkStatusAbortControllerRef.current = null;
      }
    },
  );

  const registerPollingCheckFailure = useEvent(async (): Promise<void> => {
    consecutivePollingErrorsRef.current += 1;

    if (
      consecutivePollingErrorsRef.current >=
      PRODUCE_POLLING_MAX_CONSECUTIVE_ERRORS
    ) {
      Log.warn(
        "Produce polling stopped after consecutive check failures",
        consecutivePollingErrorsRef.current,
      );
      await stopAndCleanupProducePolling();
    }
  });

  const apiCheckProduceState = useEvent(
    async (overridePollProductionId?: number): Promise<void> => {
      const polledProductionId = overridePollProductionId ?? productionPollId;
      let abortController: AbortController | null = null;

      try {
        const groupStateSnapshot =
          productionGroupStateRef.current ?? productionGroupState;

        if (isProducePollingGroupNotRunning(groupStateSnapshot)) {
          const snapshotState =
            groupStateSnapshot?.states.find(
              state => state.productionId === polledProductionId,
            ) ?? groupStateSnapshot?.states[0];
          await stopAndCleanupProducePolling(
            snapshotState ? { updatedState: snapshotState } : {},
          );
          return;
        }

        if (!polledProductionId) {
          Log.warn(
            "Missing production data on polling check id, state:",
            productionPollState,
          );
          await stopAndCleanupProducePolling();
          return;
        }

        const payload: IProduceCheckStatusPayload = {
          productionId: polledProductionId,
        };

        Log.debug(
          "Check produce status, payload:",
          payload,
          "state:",
          productionPollState,
        );

        checkStatusAbortControllerRef.current?.abort();
        abortController = new AbortController();
        checkStatusAbortControllerRef.current = abortController;

        const checkProduceStatusResult =
          await ProduceJoinApi.produceCheckStatus(payload, abortController);

        if (!checkProduceStatusResult) {
          Log.warn("Empty polling check result from api");
          await registerPollingCheckFailure();
          return;
        }

        if (!checkProduceStatusResult.success) {
          warnAndToastSimpleResult(checkProduceStatusResult, t);
          await registerPollingCheckFailure();
          return;
        }

        if (!checkProduceStatusResult.data) {
          Log.warn(
            "Invalid polling check result from api",
            checkProduceStatusResult,
          );
          await stopAndCleanupProducePolling();
          return;
        }

        const checkedState = checkProduceStatusResult.data.state;

        if (!checkedState?.productionId) {
          Log.debug("No state returned from polling iteration, skipping check");
          await registerPollingCheckFailure();
          return;
        }

        consecutivePollingErrorsRef.current = 0;

        if (checkedState.status === ProductionStatusEnum.ABORTED) {
          if (!abortToastShownRef.current) {
            Log.infoAndToast(t("rideProduction.aborted"));
            abortToastShownRef.current = true;
          }
        }

        const groupState = updateProductionGroup(
          checkedState,
          polledProductionId,
        );

        if (groupState && isProducePollingGroupNotRunning(groupState)) {
          await stopAndCleanupProducePolling({ updatedState: checkedState });
          return;
        }

        const advancedPollId = groupState?.productionPollId;

        if (
          groupState &&
          advancedPollId &&
          advancedPollId !== polledProductionId
        ) {
          await apiCheckProduceState(advancedPollId);
        }
      } catch (error) {
        if (isAbortError(error)) {
          Log.debug("Produce status polling aborted");
          return;
        }
        Log.error("Error checking produce status", error);
        await registerPollingCheckFailure();
      } finally {
        if (checkStatusAbortControllerRef.current === abortController) {
          checkStatusAbortControllerRef.current = null;
        }
      }
    },
  );

  const isProducePollingRunning = useEvent(() =>
    isProducePollingGroupRunning(
      productionGroupStateRef.current ?? productionGroupState,
    ),
  );

  const backgroundCheckProducePolling = useEvent(() => {
    startTransition(() => {
      apiCheckProduceState().catch(error => {
        Log.warn("Error in produce polling iteration", error);
      });
    });
  });

  const initProducePolling = useEvent(
    (seedGroupState?: IProductionPollGroupState): SimpleResult<void> => {
      try {
        if (seedGroupState) {
          productionGroupStateRef.current = seedGroupState;
        }

        Log.debug("Init produce polling", productionPollState);
        abortToastShownRef.current = false;
        consecutivePollingErrorsRef.current = 0;

        if (
          subscriptionProducePollingRef.current &&
          !subscriptionProducePollingRef.current.closed
        ) {
          Log.debug("Existing polling found, unsubscribing before init");
          subscriptionProducePollingRef.current.unsubscribe();
        }

        subscriptionProducePollingRef.current = createProducePollingStream({
          intervalMs: PRODUCE_POLLING_INTERVAL_MS,
          isRunning: isProducePollingRunning,
          onTick: pollingCount => {
            Log.debug(`Polling produce, polling: ${pollingCount}`);
            setPollingCount(pollingCount);
            backgroundCheckProducePolling();
          },
        }).subscribe();

        return createSuccessResultVoid();
      } catch (error) {
        void stopAndCleanupProducePolling();
        return createCatchErrorResult(error, t);
      }
    },
  );

  useEffect(() => {
    return () => {
      Log.debug("Cleanup produce polling on onmount");
      void stopAndCleanupProducePolling();
      checkStatusAbortControllerRef.current?.abort();
      checkStatusAbortControllerRef.current = null;
    };
  }, [stopAndCleanupProducePolling]);

  const refreshPollingState = useEvent(async () => {
    try {
      await apiCheckProduceState();
    } catch (error) {
      Log.error("Error refreshing logs", error);
    }
  });
  const fetchLogs = useCallback(
    async (overrideProductionId?: number): Promise<void> => {
      const targetProductionId = overrideProductionId ?? productionUiId;

      if (!targetProductionId) {
        Log.warn("Invalid production id for fetching logs");
        return;
      }

      const payload: IProduceFetchLogsPayload = {
        productionId: targetProductionId,
      };

      Log.debug("Fetching produce status, payload:", payload);

      try {
        const logDto = await ProduceJoinApi.produceFetchLogs(payload);

        if (!logDto || (logDto.success && !logDto.data)) {
          return Log.warnAndToast(t(errors.common.emptyApiResponse));
        }

        if (!logDto.success) {
          warnAndToastSimpleResult(logDto, t);
          return;
        }

        if (logDto.data?.logsRecordsCompressed) {
          const recordsJson = await gunzipBase64ToUtf8(
            logDto.data.logsRecordsCompressed,
          );
          const records = JSON.parse(recordsJson);
          setLogData({
            ...logDto.data.logsData,
            records,
          });
          return;
        }

        if (logDto.data?.logsData) {
          setLogData(logDto.data.logsData);
        }
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          warnAndToastException({
            error,
            fallbackTranslationKey: errors.common.dontHavePermissionForAction,
            translationFunction: t,
          });
          return;
        }

        if (isTranslatableItem(error)) {
          Log.warnAndToast(translateInfoItem(error, t));
        } else {
          Log.warnAndToast(t(errors.produce.failedToFetchLogs));
        }
      }
    },
    [productionUiId, setLogData, t],
  );

  return {
    initProducePolling,
    stopAndCleanupProducePolling,
    apiAbortProduceIfRunning,
    refreshPollingState,
    fetchLogs,
  };
};
